import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LiliTransaction, TransactionExternalAccountType } from '../../types/lili-transaction';
import Button from '../../../../generic-components/Button';
import { useUploadCommentMutation } from '../../../../hooks/mutations/useUploadCommentMutation';
import { useStore } from '../../../../store';
import { SpinnerColor } from '../../../../generic-components/Spinner';
import { featureAccess, LimitedFeatureType } from '../../../../services/appAccess.service';
import { FeatureAccess } from '../../../AccountSettings/components/rolesAndPermissions/PermissionsList';

interface AddNoteProps {
  transaction: LiliTransaction;
}

export const AddNote = ({ transaction }: AddNoteProps) => {
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [note, setNote] = useState('');
  const { customerId } = useStore((state) => state.user);
  const updateTransactionComment = useStore((state) => state.updateTransactionComment);
  const { accountExternalId } = useStore((state) => state.getPersonalAccount());
  const { selectedSyncedCard } = useStore((state) => state);
  const canEditNote = transaction.allowComment;
  const showEditButton = canEditNote && !editMode && note.length > 0;
  const showSaveButton = editMode && note.length > 0;
  const textAreaRef = React.createRef<HTMLTextAreaElement>();
  const transactionsAddReceiptAndNotePermission = featureAccess(null, LimitedFeatureType.TransactionsAddReceiptAndNote);

  useEffect(() => {
    setNote(transaction.comment || '');
    setCharCount(transaction.comment?.length || 0);
    setEditMode(false);
  }, [transaction]);

  const { mutateAsync: uploadComment } = useUploadCommentMutation({
    customerId,
    note,
    transactionId: transaction.transactionId,
    isExternal: transaction?.externalAccountType === TransactionExternalAccountType.PLAID,
    accountExternalId,
    externalAccountId: selectedSyncedCard?.externalId,
    handleOnSuccessUpload: (noteValue) => {
      updateTransactionComment(noteValue);
    },
  });

  const enableEditMode = () => {
    setEditMode(true);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = event.target.value;
    if (inputValue.length <= 40) {
      setCharCount(inputValue.length);
    }
    setNote(inputValue);
  };

  const handleInput = () => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'inherit';
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
  };

  const saveNote = async () => {
    if (note.length === 0) {
      setEditMode(false);
    } else {
      await uploadComment();
      setEditMode(false);
    }
  };

  const renderButton = () => {
    if (showSaveButton) {
      return (
        <Button
          submitButtonSpinnerColor={SpinnerColor.Blue}
          onClick={saveNote}
          className='font-metricR text-[14px] text-action03 mt-2'
          id='save-note-button'
          title='Save'
        />
      );
    }

    if (showEditButton) {
      return (
        <Button
          disable={transactionsAddReceiptAndNotePermission !== FeatureAccess.READ_WRITE}
          onClick={enableEditMode}
          className='font-metricR text-[14px] text-action03 mt-2'
          id='edit-note-button'
          title='Edit'
        />
      );
    }

    return null;
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveNote();
    }
  };

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.focus();

      // Put the cursor at the end of the text
      const len = textAreaRef.current.value.length;
      textAreaRef.current.setSelectionRange(len, len);
    }
  }, [editMode]);

  return (
    <div className='flex justify-between items-start bg-bw02 rounded-[6px] px-[21px] py-[16px] text-bw06 font-metricR text-[16px]'>
      <span className='inline-block grow max-w-[70%] break-words'>
        {!editMode && note.length === 0 && (
          <button
            disabled={transactionsAddReceiptAndNotePermission !== FeatureAccess.READ_WRITE}
            className='w-full h-full text-left'
            id='triggerAddNote'
            type='button'
            onClick={enableEditMode}
          >
            {t('TRANSACTION_DETAILS_ADD_NOTE')}
          </button>
        )}

        {!editMode && note.length > 0 && (
          <div className='px-2 bg-bw02 focus:outline-none resize-none w-full'>{note}</div>
        )}
        {editMode && (
          <textarea
            disabled={transactionsAddReceiptAndNotePermission !== FeatureAccess.READ_WRITE}
            ref={textAreaRef}
            className='pl-2 bg-bw02 focus:outline-none resize-none w-full'
            onChange={handleInputChange}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            maxLength={40}
            id='noteValue'
          >
            {note}
          </textarea>
        )}
      </span>
      <div className='text-[13px] flex flex-col justify-between'>
        <div id='charCount'>
          {charCount}
          /40
        </div>

        {renderButton()}
      </div>
    </div>
  );
};
